import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Scale, Shield, FileText, Database, ExternalLink, Save, Loader2,
  CheckCircle, Clock, AlertTriangle, Eye, Trash2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const STATUS_COLORS = { pending: 'text-amber-400 bg-amber-500/10', completed: 'text-[#00CC99] bg-[#00CC99]/10', rejected: 'text-[#FF6B6B] bg-red-500/10' };

export default function MasterCompliance() {
  const [settings, setSettings] = useState({ termly_id: '', iubenda_policy_id: '', iubenda_site_id: '', cookie_banner_enabled: true, do_not_sell_enabled: true });
  const [dataRequests, setDataRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('settings');

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/compliance/settings`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/privacy/data-requests`, { headers }).catch(() => ({ data: [] })),
    ]).then(([settingsRes, requestsRes]) => {
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setDataRequests(requestsRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/compliance/settings`, settings, { headers });
      toast.success('Compliance settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateRequestStatus = async (id, status) => {
    try {
      await axios.put(`${API}/privacy/data-requests/${id}`, { status }, { headers });
      setDataRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      toast.success('Request updated');
    } catch { toast.error('Failed to update'); }
  };

  const tabs = [
    { id: 'settings', label: 'Integrations', icon: Shield },
    { id: 'requests', label: 'Data Requests', icon: Database },
    { id: 'policies', label: 'Legal Pages', icon: FileText },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="master-compliance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Legal & Compliance</h1>
          <p className="text-sm text-slate-400 mt-1">Manage privacy, data protection, and legal compliance</p>
        </div>
        {tab === 'settings' && (
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all" data-testid="save-compliance-btn">
            <Save size={14} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.02] p-1 rounded-2xl border border-white/5">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all ${tab === t.id ? 'bg-gradient-to-r from-[#8B5CF6]/20 to-[#3B82F6]/10 text-white border border-[#8B5CF6]/20' : 'text-slate-400 hover:text-white'}`} data-testid={`compliance-tab-${t.id}`}>
              <Icon size={12} /> {t.label}
              {t.id === 'requests' && dataRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-1 w-4 h-4 bg-[#FF6B6B] text-white text-[9px] rounded-full flex items-center justify-center">
                  {dataRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Integration Settings Tab */}
      {tab === 'settings' && (
        <div className="space-y-5">
          {/* Termly */}
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-xs">T</div>
              <div>
                <h3 className="font-semibold text-white">Termly</h3>
                <p className="text-[11px] text-slate-500">Auto-generate compliant cookie banners and privacy policies</p>
              </div>
              <a href="https://termly.io" target="_blank" rel="noopener noreferrer" className="ml-auto text-slate-500 hover:text-white"><ExternalLink size={14} /></a>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Termly Website UUID</Label>
              <Input value={settings.termly_id} onChange={e => setSettings(p => ({ ...p, termly_id: e.target.value }))} placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="termly-id-input" />
              <p className="text-[10px] text-slate-500 mt-1.5">Find this in your Termly dashboard under Website Settings. When set, Termly's cookie banner will replace the default one.</p>
            </div>
          </GlassCard>

          {/* Iubenda */}
          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-xs">iu</div>
              <div>
                <h3 className="font-semibold text-white">Iubenda</h3>
                <p className="text-[11px] text-slate-500">Generate privacy & cookie policies, consent management</p>
              </div>
              <a href="https://www.iubenda.com" target="_blank" rel="noopener noreferrer" className="ml-auto text-slate-500 hover:text-white"><ExternalLink size={14} /></a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">Iubenda Policy ID</Label>
                <Input value={settings.iubenda_policy_id} onChange={e => setSettings(p => ({ ...p, iubenda_policy_id: e.target.value }))} placeholder="e.g., 12345678" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="iubenda-policy-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Iubenda Site ID</Label>
                <Input value={settings.iubenda_site_id} onChange={e => setSettings(p => ({ ...p, iubenda_site_id: e.target.value }))} placeholder="e.g., 987654" className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm" data-testid="iubenda-site-input" />
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">Find these in your Iubenda dashboard. When set, Iubenda will handle cookie consent and policy generation.</p>
          </GlassCard>

          {/* Banner Controls */}
          <GlassCard className="p-6">
            <h3 className="font-semibold text-white mb-4">Platform Defaults</h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5 cursor-pointer">
                <div>
                  <p className="text-sm text-white">Built-in Cookie Consent Banner</p>
                  <p className="text-[10px] text-slate-500">Show the default cookie consent banner (disabled when Termly or Iubenda is configured)</p>
                </div>
                <input type="checkbox" checked={settings.cookie_banner_enabled} onChange={e => setSettings(p => ({ ...p, cookie_banner_enabled: e.target.checked }))} className="w-4 h-4 rounded" data-testid="cookie-banner-toggle" />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5 cursor-pointer">
                <div>
                  <p className="text-sm text-white">"Do Not Sell" Page</p>
                  <p className="text-[10px] text-slate-500">Enable the CCPA "Do Not Sell My Personal Information" opt-out page</p>
                </div>
                <input type="checkbox" checked={settings.do_not_sell_enabled} onChange={e => setSettings(p => ({ ...p, do_not_sell_enabled: e.target.checked }))} className="w-4 h-4 rounded" data-testid="dns-toggle" />
              </label>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Data Requests Tab */}
      {tab === 'requests' && (
        <div className="space-y-4" data-testid="data-requests-tab">
          {dataRequests.length > 0 ? (
            dataRequests.map(req => (
              <GlassCard key={req.id} className="p-4 flex items-center gap-4" data-testid={`data-request-${req.id}`}>
                <div className={`p-2 rounded-xl ${req.request_type === 'deletion' ? 'bg-red-500/10' : req.request_type === 'download' ? 'bg-blue-500/10' : 'bg-[#8B5CF6]/10'}`}>
                  {req.request_type === 'deletion' ? <Trash2 size={16} className="text-[#FF6B6B]" /> : req.request_type === 'download' ? <Database size={16} className="text-blue-400" /> : <Eye size={16} className="text-[#A78BFA]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{req.email}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full capitalize bg-white/5 text-slate-400 border border-white/10">{req.request_type}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[req.status] || 'text-slate-400 bg-white/5'}`}>{req.status}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">Submitted: {new Date(req.created_at).toLocaleDateString()}</p>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-1.5 shrink-0">
                    <button onClick={() => updateRequestStatus(req.id, 'completed')} className="px-3 py-1.5 text-xs bg-[#00CC99]/10 text-[#00CC99] border border-[#00CC99]/20 rounded-lg hover:bg-[#00CC99]/20 transition-colors" data-testid={`complete-request-${req.id}`}>
                      Complete
                    </button>
                    <button onClick={() => updateRequestStatus(req.id, 'rejected')} className="px-3 py-1.5 text-xs bg-red-500/10 text-[#FF6B6B] border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors" data-testid={`reject-request-${req.id}`}>
                      Reject
                    </button>
                  </div>
                )}
              </GlassCard>
            ))
          ) : (
            <GlassCard className="p-12 text-center">
              <Database size={40} className="mx-auto text-slate-600 mb-3" />
              <h3 className="font-semibold text-white mb-2">No Data Requests</h3>
              <p className="text-slate-400 text-sm">No user data requests have been submitted yet.</p>
            </GlassCard>
          )}
        </div>
      )}

      {/* Legal Pages Tab */}
      {tab === 'policies' && (
        <div className="space-y-4" data-testid="legal-pages-tab">
          {[
            { path: '/privacy', title: 'Privacy Policy', desc: 'Explains data collection, storage, CCPA rights, third-party processors' },
            { path: '/terms', title: 'Terms of Service', desc: 'Platform rules, SaaS vs Marketplace separation, payment processing, liability' },
            { path: '/acceptable-use', title: 'Acceptable Use Policy', desc: 'Prohibited content, IP rules, payment compliance, enforcement' },
            { path: '/do-not-sell', title: 'Do Not Sell My Info', desc: 'CCPA opt-out form for users to prevent data sharing' },
            { path: '/data-request', title: 'Data Request Form', desc: 'CCPA data access, download, and deletion request form' },
          ].map(page => (
            <GlassCard key={page.path} className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-xl bg-[#8B5CF6]/10"><FileText size={16} className="text-[#A78BFA]" /></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{page.title}</p>
                <p className="text-[10px] text-slate-500">{page.desc}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={page.path} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/10 text-slate-300 rounded-lg hover:bg-white/10 transition-colors" data-testid={`view-${page.path.slice(1)}`}>
                  <Eye size={12} /> View
                </a>
              </div>
            </GlassCard>
          ))}
          <GlassCard className="p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-amber-500/10 shrink-0"><AlertTriangle size={14} className="text-amber-400" /></div>
              <div>
                <p className="text-xs text-slate-300">Legal pages use sensible defaults. To customize the content for your specific business, connect <strong className="text-white">Termly</strong> or <strong className="text-white">Iubenda</strong> in the Integrations tab — they auto-generate legally compliant policies.</p>
              </div>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}
